import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Trophy, TrendingUp, Users, Coins, Crown, Medal, Award, Star, Flame } from "lucide-react";

export default function LeaderboardPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("artists");
  const [artists, setArtists] = useState([]);
  const [topFans, setTopFans] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchArtists = useCallback(async () => {
    try {
      const res = await api.get("/leaderboard");
      setArtists(res.data.artists);
    } catch (err) {
      console.error(err);
    }
  }, [api]);

  const fetchTopFans = useCallback(async () => {
    try {
      const res = await api.get("/topfan/leaderboard/global");
      setTopFans(res.data.top_fans || []);
    } catch (err) {
      console.error(err);
    }
  }, [api]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchArtists(), fetchTopFans()]).finally(() => setLoading(false));
  }, [fetchArtists, fetchTopFans]);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.5)]" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
    if (rank === 3) return <Award className="w-5 h-5 text-amber-600" />;
    return <span className="font-mono-credits font-bold text-sm text-muted-foreground w-6 text-center">{rank}</span>;
  };

  return (
    <div className="py-6 space-y-6" data-testid="leaderboard-page">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full px-4 py-1.5 mb-3">
          <Trophy className="w-4 h-4 text-[#FF6B35]" />
          <span className="text-xs font-bold text-[#FF6B35]">LE TOP DU DIMANCHE</span>
        </div>
        <h1 className="font-heading font-extrabold text-2xl text-foreground">Classement</h1>
        <p className="text-sm text-muted-foreground mt-1">Les artistes et fans les plus actifs</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-popover rounded-2xl p-1.5" data-testid="leaderboard-tabs">
        <button
          onClick={() => setTab("artists")}
          className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${
            tab === "artists"
              ? "bg-[#FF6B35] text-white shadow-lg shadow-[#FF6B35]/30"
              : "text-muted-foreground hover:text-[#AFA8BA]"
          }`}
          data-testid="tab-artists"
        >
          <TrendingUp className="w-4 h-4 inline mr-1.5" />
          Artistes
        </button>
        <button
          onClick={() => setTab("fans")}
          className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${
            tab === "fans"
              ? "bg-[#FF6B35] text-white shadow-lg shadow-[#FF6B35]/30"
              : "text-muted-foreground hover:text-[#AFA8BA]"
          }`}
          data-testid="tab-fans"
        >
          <Flame className="w-4 h-4 inline mr-1.5" />
          Top Fans
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-foreground/5 rounded-3xl h-20 animate-pulse" />
          ))}
        </div>
      ) : tab === "artists" ? (
        /* ARTISTS TAB */
        artists.length === 0 ? (
          <div className="text-center py-16">
            <Trophy className="w-14 h-14 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Aucun artiste dans le classement</p>
          </div>
        ) : (
          <div className="space-y-3">
            {artists.map((artist) => (
              <div
                key={artist.id}
                onClick={() => navigate(`/artist/${artist.id}`)}
                className={`bg-card border rounded-3xl p-4 cursor-pointer hover:scale-[1.01] transition-all duration-200 shadow-sm ${
                  artist.rank === 1 ? "border-yellow-300" :
                  artist.rank === 2 ? "border-gray-300" :
                  artist.rank === 3 ? "border-amber-400/50" : "border-border"
                }`}
                data-testid={`leaderboard-rank-${artist.rank}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-8 flex justify-center shrink-0">
                    {getRankIcon(artist.rank)}
                  </div>
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#7000FF] flex items-center justify-center shrink-0 ${
                    artist.rank <= 3 ? "shadow-lg shadow-[#FF6B35]/20" : ""
                  }`}>
                    {artist.avatar_url ? (
                      <img src={artist.avatar_url} alt="" className="w-full h-full rounded-2xl object-cover" />
                    ) : (
                      <span className="text-white font-heading font-bold text-lg">
                        {(artist.name || "?")[0].toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-heading font-bold truncate ${
                      artist.rank === 1 ? "text-yellow-600" : "text-card-foreground"
                    }`}>
                      {artist.name}
                    </h3>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Users className="w-3 h-3" />
                        {artist.fan_count} fans
                      </span>
                      {artist.genres?.length > 0 && (
                        <span className="text-xs text-muted-foreground">{artist.genres[0]}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="flex items-center gap-1">
                      <TrendingUp className={`w-4 h-4 ${
                        artist.current_score >= 1.5 ? "text-emerald-500" :
                        artist.current_score >= 1.0 ? "text-[#FF6B35]" : "text-red-500"
                      }`} />
                      <span className={`font-mono-credits font-bold text-lg ${
                        artist.current_score >= 1.5 ? "text-emerald-500" :
                        artist.current_score >= 1.0 ? "text-[#FF6B35]" : "text-red-500"
                      }`}>
                        {artist.current_score?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 justify-end mt-0.5">
                      <Coins className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground font-mono-credits">
                        {artist.total_bets || 0} mises
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        /* TOP FANS TAB */
        topFans.length === 0 ? (
          <div className="text-center py-16">
            <Star className="w-14 h-14 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground font-medium">Aucun Top Fan cette semaine</p>
            <p className="text-xs text-muted-foreground mt-1">Interagis avec tes artistes preferes pour monter au classement !</p>
          </div>
        ) : (
          <div className="space-y-3">
            {topFans.map((fan) => (
              <div
                key={fan.user_id}
                className={`bg-card border rounded-3xl p-4 transition-all duration-200 shadow-sm ${
                  fan.is_me ? "border-yellow-300 ring-2 ring-yellow-200" :
                  fan.rank === 1 ? "border-yellow-300" :
                  fan.rank === 2 ? "border-gray-300" :
                  fan.rank === 3 ? "border-amber-400/50" : "border-border"
                }`}
                data-testid={`topfan-rank-${fan.rank}`}
              >
                <div className="flex items-center gap-4">
                  {/* Rank */}
                  <div className="w-8 flex justify-center shrink-0">
                    {getRankIcon(fan.rank)}
                  </div>

                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#7000FF] flex items-center justify-center shrink-0 overflow-hidden relative">
                    {fan.avatar_url ? (
                      <img src={fan.avatar_url} alt="" className="w-full h-full rounded-2xl object-cover" />
                    ) : (
                      <span className="text-white font-heading font-bold text-lg">
                        {(fan.pseudo || "?")[0].toUpperCase()}
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/user/${fan.user_id}`}
                        className={`font-heading font-bold truncate hover:text-[#FF6B35] transition-colors ${
                          fan.is_me ? "text-yellow-600" : "text-card-foreground"
                        }`}
                      >
                        {fan.is_me ? `${fan.pseudo} (Toi)` : fan.pseudo}
                      </Link>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {fan.artists?.map((a, i) => (
                        <span key={i} className="text-[10px] bg-[#FF6B35]/10 text-[#FF6B35] px-2 py-0.5 rounded-full font-medium">
                          {a.artist_name}
                        </span>
                      ))}
                      {fan.artist_count > 3 && (
                        <span className="text-[10px] text-muted-foreground">+{fan.artist_count - 3}</span>
                      )}
                    </div>
                  </div>

                  {/* Score */}
                  <div className="text-right shrink-0">
                    <div className="flex items-center gap-1 justify-end">
                      <Flame className="w-4 h-4 text-[#FF6B35]" />
                      <span className="font-mono-credits font-bold text-lg text-[#FF6B35]">
                        {fan.total_score}
                      </span>
                    </div>
                    <span className="text-[10px] text-muted-foreground">
                      {fan.artist_count} artiste{fan.artist_count > 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
